import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateInterviewQuestions } from '../services/openaiService';
import { createDoc, PDF, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawMetaCard, drawSectionHeading } from '../utils/pdfBrand';
import Header from './Header';
import Footer from './Footer';
import './InterviewQuestionsPage.css';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const InterviewQuestionsPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();
  
  const [jobDescription, setJobDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [error, setError] = useState('');
  const [parseError, setParseError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isResumeUploaded, setIsResumeUploaded] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  const handleJobDescriptionChange = (e) => {
    setJobDescription(e.target.value);
    setError('');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsParsingFile(true);
    setParseError('');
    setUploadedFileName(file.name);

    try {
      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsText(file);
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          fullText += textContent.items.map(item => item.str).join(' ') + '\n';
        }
        extractedText = fullText.trim();
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx') ||
        file.type === 'application/msword' ||
        file.name.endsWith('.doc')
      ) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        extractedText = result.value;
      } else {
        throw new Error('Unsupported file format. Please upload .pdf, .docx, or .txt files.');
      }

      extractedText = extractedText.trim();
      if (!extractedText) throw new Error('No text could be extracted from the file.');

      setResumeText(extractedText);
      setIsResumeUploaded(true);
      setParseError('');
    } catch (error) {
      console.error('Error processing file:', error);
      setParseError(error.message || 'Failed to process file.');
      setIsResumeUploaded(false);
      setUploadedFileName('');
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-questions/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setShowInsufficientCreditsModal(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }

      if (setCredits) {
        setCredits(data.remainingCredits);
      }
      
      return true;
    } catch (error) {
      console.error('Error deducting credit:', error);
      setError('Failed to process credit transaction');
      return false;
    }
  };

  const saveToDatabase = async (generatedQuestions) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-questions/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          jobTitle,
          companyName,
          jobDescription,
          questions: generatedQuestions,
          resumeFilename: uploadedFileName
        })
      });

      if (response.ok) {
        console.log('Interview questions saved successfully');
      }
    } catch (saveError) {
      console.error('Error saving interview questions:', saveError);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!user) {
      setShowAuthModal(true);
      setError('Please login to generate interview questions');
      return;
    }

    if (!resumeText.trim()) {
      setError('Please upload your resume');
      return;
    }

    if (!jobDescription.trim()) {
      setError('Please enter the job description');
      return;
    }

    if (!jobTitle.trim()) {
      setError('Please enter the job title');
      return;
    }

    if (credits < 1) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const creditDeducted = await deductCredit();

      if (!creditDeducted) {
        setIsGenerating(false);
        return;
      }

      const generatedQuestions = await generateInterviewQuestions(
        resumeText,
        jobDescription,
        jobTitle,
        companyName
      );

      setQuestions(generatedQuestions);

      await saveToDatabase(generatedQuestions);

      if (fetchCredits) {
        fetchCredits();
      }
    } catch (error) {
      console.error('Error generating interview questions:', error);
      setError('Failed to generate interview questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearAll = () => {
    setJobDescription('');
    setJobTitle('');
    setCompanyName('');
    setResumeText('');
    setUploadedFileName('');
    setQuestions([]);
    setError('');
    setParseError('');
    setIsResumeUploaded(false);
    setExpandedQuestions(new Set());
    setResumeChoice(null);
  };

  const toggleQuestion = (index) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleCopyAll = () => {
    const allText = questions.map((q, i) => 
      `Question ${i + 1}: ${q.question}\n\nAnswer: ${q.answer}\n\n---\n\n`
    ).join('');
    navigator.clipboard.writeText(allText);
    alert('All questions and answers copied to clipboard!');
  };

  const handleDownloadPDF = () => {
    try {
      const doc = createDoc();
      const { W, H, M, CW } = PDF;
      let y = 0;

      const expLabel = '';
      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      // White bg page 1
      fR(doc, 255, 255, 255, 0, 0, W, H);

      // Branded header
      drawCoverHeader(doc, {
        reportTitle: 'Interview Questions & Answers',
        role: jobTitle,
        company: companyName,
        levelLabel: expLabel,
        dateStr,
      });
      y = 62;

      // Metadata card
      y = drawMetaCard(doc, [
        { label: 'Position', value: jobTitle },
        { label: 'Company', value: companyName },
        { label: 'Experience Level', value: expLabel },
        { label: 'Total Questions', value: questions.length },
        { label: 'Date', value: dateStr },
      ], y) + 12;

      // Section heading
      y = drawSectionHeading(doc, `Interview Questions  ·  ${questions.length} questions`, y);

      const addNewPage = () => {
        doc.addPage();
        fR(doc, 255, 255, 255, 0, 0, W, H);
        drawPageHeader(doc, jobTitle);
        y = 21;
      };
      const checkBreak = (need) => { if (y + need > H - 16) addNewPage(); };

      questions.forEach((q, index) => {
        const qLines = doc.splitTextToSize(q.question || '', CW - 20);
        const ansLines = doc.splitTextToSize(q.answer || '', CW - 14);
        const ansBoxH = ansLines.length * 5 + 13;
        checkBreak(qLines.length * 5 + ansBoxH + 18);

        // Question header bar
        fR(doc, 22, 14, 52, M, y, CW, 10 + (qLines.length - 1) * 5);
        fR(doc, 109, 40, 217, M, y, 4, 10 + (qLines.length - 1) * 5);
        T(doc, [196, 181, 253], 8, true, `Q${String(index + 1).padStart(2, '0')}`, M + 7, y + 7);
        T(doc, [230, 225, 248], 8, false, qLines, M + 18, y + 7);
        y += 10 + (qLines.length - 1) * 5 + 3;

        // Answer card
        checkBreak(ansBoxH);
        fR(doc, 246, 244, 255, M, y, CW, ansBoxH);
        sR(doc, 218, 213, 238, 0.3, M, y, CW, ansBoxH);
        fR(doc, 109, 40, 217, M, y, 3.5, ansBoxH);
        T(doc, [76, 29, 149], 7, true, 'ANSWER', M + 7, y + 6);
        T(doc, [35, 28, 55], 8, false, ansLines, M + 7, y + 12);
        y += ansBoxH + 10;
      });

      drawFooters(doc);

      const fileName = `terview-questions-${(jobTitle || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className={embedded ? 'iq-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      <div
        className={`interview-questions-page-container ${embedded ? 'interview-questions-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/interview-questions.png')" }}
      >
        {!embedded && <Header setShowAuthModal={setShowAuthModal} />}

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to generate interview questions.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="interview-questions-page-main">
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                Interview <span className="li-gradient-text">Questions Generator</span>
              </h1>
              <p className="li-hero-subtitle">
                Train with AI-powered interview questions and prepare for your next interview.
              </p>
            </div>
          )}
          <div className="interview-questions-main-container">
            <div className="interview-questions-content">
              <div className="left-panel">
                <div className="job-details-section">
                  <div className="job-details-container glass">
                    <div className="section-header">
                      <h3>Job Details</h3>
                      <button 
                        onClick={handleClearAll} 
                        className="clear-button"
                        disabled={isGenerating || isParsingFile}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="form-grid-iq">
                      <div className="form-group-iq">
                        <label>Job Title *</label>
                        <input
                          type="text"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          placeholder="e.g., Senior Software Engineer"
                          disabled={isGenerating || isParsingFile}
                        />
                      </div>
                      <div className="form-group-iq">
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="e.g., Tech Corp"
                          disabled={isGenerating || isParsingFile}
                        />
                      </div>
                      <div className="form-group-iq full-width">
                        <label>Job Description *</label>
                        <textarea
                          value={jobDescription}
                          onChange={handleJobDescriptionChange}
                          placeholder="Paste the complete job description here..."
                          rows="8"
                          disabled={isGenerating || isParsingFile}
                        ></textarea>
                      </div>

                      <div className="form-group-iq full-width file-upload-inline">
                        <label className="form-group-iq" style={{ marginBottom: '8px' }}>Your Resume *</label>

                        {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                          <>
                            <div className="resume-choice-buttons-iq">
                              <button
                                type="button"
                                onClick={() => {
                                  setResumeChoice('profile');
                                  const text = buildResumeTextFromProfile(profileData);
                                  setResumeText(text);
                                  setUploadedFileName(`${profileData.fullName || 'Profile'} (Profile Resume)`);
                                  setIsResumeUploaded(true);
                                  setParseError('');
                                }}
                                className={`resume-choice-btn-iq${resumeChoice === 'profile' ? ' resume-choice-btn-iq-active' : ''}`}
                                disabled={isParsingFile || isGenerating}
                              >
                                <div className="resume-choice-label-iq">✅ Use Profile Resume</div>
                                {profileData.fullName && <div className="resume-choice-sublabel-iq">{profileData.fullName}</div>}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setResumeChoice('upload');
                                  setResumeText('');
                                  setUploadedFileName('');
                                  setIsResumeUploaded(false);
                                }}
                                className={`resume-choice-btn-iq${resumeChoice === 'upload' ? ' resume-choice-btn-iq-active' : ''}`}
                                disabled={isParsingFile || isGenerating}
                              >
                                <div className="resume-choice-label-iq">📄 Upload New Resume</div>
                                <div className="resume-choice-sublabel-iq">Choose a different file</div>
                              </button>
                            </div>

                            {resumeChoice === 'profile' && (
                              <div className="profile-resume-confirm-iq">
                                ✓ Profile resume loaded and ready to use.
                              </div>
                            )}

                            {resumeChoice === 'upload' && (
                              <div className="upload-area-iq">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept=".pdf,.docx,.doc"
                                  onChange={handleFileUpload}
                                  disabled={isParsingFile || isGenerating}
                                  style={{ display: 'none' }}
                                />
                                <button
                                  onClick={() => fileInputRef.current?.click()}
                                  className="file-upload-label-iq"
                                  disabled={isParsingFile || isGenerating}
                                >
                                  {isParsingFile ? (
                                    <span className="loading">
                                      <span className="typing-indicator"><span></span><span></span><span></span></span>
                                      Processing Resume...
                                    </span>
                                  ) : 'Upload Resume (PDF/DOCX)'}
                                </button>
                                <p className="upload-hint-iq">
                                  {uploadedFileName ? `✓ ${uploadedFileName}` : 'Upload your resume to generate personalized questions'}
                                </p>
                              </div>
                            )}

                            {!resumeChoice && (
                              <p className="upload-hint-iq">Select an option above to provide your resume</p>
                            )}
                          </>
                        ) : (
                          <div className="upload-area-iq">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf,.docx,.doc"
                              onChange={handleFileUpload}
                              disabled={isParsingFile || isGenerating}
                              style={{ display: 'none' }}
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="file-upload-label-iq"
                              disabled={isParsingFile || isGenerating}
                            >
                              {isParsingFile ? (
                                <span className="loading">
                                  <span className="typing-indicator"><span></span><span></span><span></span></span>
                                  Processing Resume...
                                </span>
                              ) : 'Upload Resume (PDF/DOCX)'}
                            </button>
                            <p className="upload-hint-iq">
                              {uploadedFileName ? `✓ ${uploadedFileName}` : 'Upload your resume to generate personalized questions'}
                            </p>
                          </div>
                        )}

                        {parseError && <div className="parse-error-iq">{parseError}</div>}

                        {resumeChoice !== null && (
                          <div className="form-group-iq full-width resume-data" style={{ marginTop: '10px' }}>
                            <textarea
                              value={resumeText}
                              onChange={(e) => setResumeText(e.target.value)}
                              placeholder="Resume content will appear here after upload..."
                              rows="8"
                              disabled={isGenerating || isParsingFile}
                            ></textarea>
                          </div>
                        )}

                        <div className="generate-section">
                          <button
                            onClick={handleGenerateQuestions}
                            className="generate-btn-main"
                            disabled={isGenerating || !isResumeUploaded || !resumeText.trim() || !jobDescription.trim() || !jobTitle.trim() || !user}
                          >
                            {isGenerating ? (
                              <span className="loading">
                                <span className="typing-indicator">
                                  <span></span><span></span><span></span>
                                </span>
                                Generating Questions...
                              </span>
                            ) : !user ? 'Login to Generate Questions' : !isResumeUploaded ? 'Upload Resume to Generate' : 'Generate Interview Questions (1 Credit)'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="right-panel">
                <div className="questions-output-section">
                  <div className="questions-container glass">
                    <div className="section-header">
                      <h3>Interview Questions ({questions.length})</h3>
                      <div className="action-buttons">
                        <button 
                          onClick={handleCopyAll} 
                          className="copy-button"
                          disabled={isGenerating || questions.length === 0}
                        >
                          Copy All
                        </button>
                        <button 
                          onClick={handleDownloadPDF} 
                          className="download-button"
                          disabled={isGenerating || questions.length === 0}
                        >
                          Download PDF
                        </button>
                      </div>
                    </div>
                    <div className="questions-list">
                      {isGenerating ? (
                        <div className="loading-placeholder">
                          <span className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </span>
                          Generating 50 personalized interview questions...
                        </div>
                      ) : questions.length > 0 ? (
                        questions.map((q, index) => (
                          <div key={index} className="question-item">
                            <div
                              className="question-header"
                              onClick={() => toggleQuestion(index)}
                            >
                              <span className="question-number">Q{index + 1}</span>
                              <span className="question-text">{q.question}</span>
                              <span className="expand-icon">
                                {expandedQuestions.has(index) ? '−' : '+'}
                              </span>
                            </div>
                            {expandedQuestions.has(index) && (
                              <div className="answer-section">
                                <strong>Answer:</strong>
                                <p>{q.answer}</p>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="placeholder">
                          Your personalized interview questions will appear here...
                          <br /><br />
                          <small>Quick steps:</small>
                          <ul>
                            <li>Fill in job details and description</li>
                            <li>Upload your resume</li>
                            <li>Click "Generate Interview Questions"</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message glass">
                {error}
              </div>
            )}
          </div>

          {!embedded && (
          <>
          <section className="info-section glass">
            <h2 className="section-title">Why Use Our Interview Question Generator?</h2>
            <div className="info-grid">
              <div className="info-card glass">
                <div className="info-icon">🎯</div>
                <h3>Personalized Questions</h3>
                <p>AI analyzes your resume and job description to generate 50 tailored interview questions specific to your background and the role you're applying for.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">💡</div>
                <h3>Smart Answers</h3>
                <p>Each question comes with a detailed answer based on your actual experience, helping you prepare confident responses for your interview.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">⚡</div>
                <h3>Interview Ready</h3>
                <p>Practice with realistic questions covering technical skills, behavioral scenarios, and role-specific topics to ace your interview.</p>
              </div>
            </div>
          </section>

          <section className="how-it-works glass">
            <h2 className="section-title">How It Works</h2>
            <div className="steps-grid">
              <div className="step-card glass">
                <div className="step-number">1</div>
                <h4>Enter Job Details</h4>
                <p>Provide the job title, company name, and paste the complete job description.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">2</div>
                <h4>Upload Your Resume</h4>
                <p>Upload your resume so our AI can understand your experience and skills.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">3</div>
                <h4>Generate Questions</h4>
                <p>Our AI creates 50 personalized interview questions with tailored answers.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">4</div>
                <h4>Practice & Prepare</h4>
                <p>Review questions and answers to prepare for your upcoming interview.</p>
              </div>
            </div>
          </section>

          {/* tips-section */}
          <section className="tips-section glass">
            <h2 className="section-title">Interview Preparation Tips</h2>
            <div className="tips-grid">
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Practice Out Loud</h4>
                </div>
                <p>Don't just read the answers mentally. Practice speaking them out loud to build confidence and improve delivery.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Customize Your Answers</h4>
                </div>
                <p>Use the generated answers as a foundation, then personalize them with your own voice and additional examples.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Research the Company</h4>
                </div>
                <p>Supplement these questions with company-specific research to show genuine interest in the organization.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Prepare Questions</h4>
                </div>
                <p>Have thoughtful questions ready to ask the interviewer about the role, team, and company culture.</p>
              </div>
            </div>
          </section>
          </>
          )}
        </main>

        {!embedded && <Footer />}
      </div>
    </div>
  );
};

export default InterviewQuestionsPage;